import axios from "axios";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { BsThreeDots } from "react-icons/bs";
import BaseUrl from "../../Components/BaseUrl";
import Cookies from "js-cookie";

function getTimeAgo(dateString) {
  const now = new Date();
  const past = new Date(dateString);
  const diffMs = now - past;

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) return `${years}y`;
  if (months > 0) return `${months}mo`;
  if (weeks > 0) return `${weeks}w`;
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}
const getColorFromName = (name) => {
  if (!name) return { backgroundColor: "hsl(0, 0%, 50%)" };

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash) % 360;
  const saturation = 70 + (Math.abs(hash) % 30); // 70–99%
  const lightness = 50 + (Math.abs(hash) % 10);

  return {
    backgroundColor: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
  };
};

const NotificationItem = ({
  id,
  icon,
  text,
  time,
  name,
  status,
  handleReadMessage,
  handleDeleteNotification,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  return (
    <div
      className={`flex items-center justify-between p-3 my-2 bg-white rounded-lg cursor-pointer ${
        status ? "" : "bg-blue-200"
      }`}
      onClick={() => {
        if (!status) {
          handleReadMessage(id);
        }
      }}
    >
      <div className="flex items-center cursor-pointer">
        {icon ? (
          <img src={icon} alt={name} className="w-10 h-10 rounded-full mr-3" />
        ) : (
          <div
            style={getColorFromName(name || "?")}
            className="w-10 h-10 rounded-full flex items-center justify-center text-white mr-3 font-bold text-sm"
          >
            {(name || "?").charAt(0).toUpperCase()}
          </div>
        )}

        <div>
          <p className="text-sm text-gray-800 font-medium">
            {name} {text}
          </p>
        </div>
      </div>
      <div className="relative" ref={dropdownRef}>
        <div className="flex items-center">
          <p className="text-xs text-gray-500 mt-1 mr-2">{getTimeAgo(time)}</p>
          <button
            onClick={toggleDropdown}
            className="text-lg font-bold text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <BsThreeDots />
          </button>
        </div>

        {showDropdown && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
            <div className="py-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteNotification(id);
                  setShowDropdown(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Delete Notification
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Notifications = () => {
  const [recentCount, setRecentCount] = useState(0);
  const [earlierCount, setEarlierCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const pollingRef = useRef(null);

  const handleReadMessage = async (messageId) => {
    try {
      const response = await axios.post(
        BaseUrl + "readNotification",
        {
          messageId: messageId,
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setNotifications((prevNotifications) =>
          prevNotifications.map((notif) =>
            notif.id === messageId ? { ...notif, read_status: 1 } : notif
          )
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      const response = await axios.post(
        BaseUrl + "deleteNotification",
        {
          notificationId: notificationId,
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setNotifications((prevNotifications) =>
          prevNotifications.filter((notif) => notif.id !== notificationId)
        );
        const recent = notifications.filter(
          (n) => n.type === "recent" && n.id !== notificationId
        ).length;
        const earlier = notifications.filter(
          (n) => n.type === "earlier" && n.id !== notificationId
        ).length;

        setRecentCount(recent);
        setEarlierCount(earlier);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getNotifications = useCallback(async () => {
    try {
      const response = await axios.get(BaseUrl + "getUserNotifications", {
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
      });

      if (response.data.status === 200) {
        setNotifications((prevNotifications) => {
          const newNotifications = response.data.data.map((notif) => {
            const createdAtUTC = new Date(notif.created_at);
            const now = new Date();
            const diffInMs = now - createdAtUTC;
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

            return {
              ...notif,
              time: notif.created_at,
              type: diffInMinutes <= 5 ? "recent" : "earlier",
            };
          });

          if (isFirstLoad) {
            setIsFirstLoad(false);
            setIsLoading(false);
            return newNotifications;
          }

          const existingIds = new Set(prevNotifications.map((n) => n.id));
          const trulyNewNotifications = newNotifications.filter(
            (n) => !existingIds.has(n.id)
          );
          if (trulyNewNotifications.length === 0) {
            return prevNotifications;
          }
          return [...trulyNewNotifications, ...prevNotifications];
        });


        setRecentCount((prev) => {
          const newRecent = response.data.data.filter((n) => {
            const diffInMinutes = Math.floor(
              (new Date() - new Date(n.created_at)) / (1000 * 60)
            );
            return diffInMinutes <= 5;
          }).length;
          return newRecent;
        });

        setEarlierCount((prev) => {
          const newEarlier = response.data.data.filter((n) => {
            const diffInMinutes = Math.floor(
              (new Date() - new Date(n.created_at)) / (1000 * 60)
            );
            return diffInMinutes > 5;
          }).length;
          return newEarlier;
        });
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [isFirstLoad]);


  useEffect(() => {

    getNotifications();

    pollingRef.current = setInterval(getNotifications, 5000);


    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [getNotifications]);

  // useEffect(() => {
  //   getNotifications();
  // }, []);

  // const getNotifications = async () => {
  //   try {
  //     const response = await axios.get(BaseUrl + "getUserNotifications", {
  //       headers: {
  //         Authorization: `Bearer ${Cookies.get("token")}`,
  //       },
  //     });
  //     if (response.data.status === 200) {
  //       const updatedNotifications = response.data.data.map((notif) => {
  //         const createdAtUTC = new Date(notif.created_at);
  //         const now = new Date();
  //         const diffInMs = now - createdAtUTC;
  //         const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  //         return {
  //           ...notif,
  //           time: notif.created_at,
  //           type: diffInMinutes <= 5 ? "recent" : "earlier",
  //         };
  //       });
  //       const recent = updatedNotifications.filter(n => n.type === "recent").length;
  //       const earlier = updatedNotifications.filter(n => n.type === "earlier").length;
  //       setNotifications(updatedNotifications);
  //       setRecentCount(recent);
  //       setEarlierCount(earlier);
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };
  const MemoizedNotificationItem = React.memo(NotificationItem);

  // return (
  //   <div className="bg-[#F3F2EF] mt-16 py-6 min-h-screen">
  //     <div className="container mx-auto px-4 sm:px-6 lg:px-8">
  //       <div className="flex flex-col lg:flex-row gap-6">
  //         {/* Left Sidebar */}
  //         <div className="w-full lg:w-1/4 h-fit bg-white p-6 rounded-lg shadow-md">
  //           <h2 className="text-base font-semibold mb-4">
  //             Manage your Notifications
  //           </h2>

  //           <div className="flex items-start gap-1 mb-4">
  //             <span className="text-2xl !font-black !leading-3 !text-black">
  //               &rarr;
  //             </span>
  //             <p className="text-sm text-gray-600">
  //               We'll keep your responses confidential and secure. They won't be
  //               shared with recruiters and employers.
  //             </p>
  //           </div>

  //           <div className="flex items-start gap-1">
  //             <span className="text-2xl font-black !leading-3 text-black">
  //               &rarr;
  //             </span>
  //             <p className="text-sm text-gray-600">
  //               We'll keep your responses confidential and secure. They won't be
  //               shared with recruiters and employers.
  //             </p>
  //           </div>

  //           <a href="#" className="text-blue-600 text-sm mt-4 inline-block">
  //             View Settings
  //           </a>
  //         </div>

  //         {/* Notifications Section */}
  //         <div className="w-full lg:w-3/4">
  //           {recentCount > 0 && (
  //             <div className="px-6">
  //               <div className="flex items-center gap-8">
  //                 <hr className="w-full border-t border-gray-300 " />
  //                 <h2 className="text-lg text-center font-semibold">Recent</h2>
  //                 <hr className="w-full border-t border-gray-300 " />
  //               </div>
  //               {notifications
  //                 .filter((n) => n.type === "recent")
  //                 .map((n) => (
  //                   <NotificationItem
  //                     key={n.id}
  //                     id={n.id}
  //                     icon={n.sender.profile}
  //                     text={n.message}
  //                     time={n.time}
  //                     name={n.sender.name}
  //                     status={n.read_status}
  //                     handleReadMessage={handleReadMessage}
  //                     handleDeleteNotification={handleDeleteNotification}
  //                   />
  //                 ))}
  //             </div>
  //           )}
  //           {earlierCount > 0 && (
  //             <div className="px-6 py-2">
  //               <div className="flex items-center gap-8">
  //                 <hr className="w-full border-t border-gray-300" />
  //                 <h2 className="text-lg text-centter font-semibold">
  //                   Earlier
  //                 </h2>
  //                 <hr className="w-full border-t border-gray-300 " />
  //               </div>
  //               {notifications
  //                 .filter((n) => n.type === "earlier")
  //                 .map((n) => (
  //                   <NotificationItem
  //                     key={n.id}
  //                     id={n.id}
  //                     icon={n.sender.profile}
  //                     text={n.message}
  //                     time={n.time}
  //                     name={n.sender.name}
  //                     status={n.read_status}
  //                     handleReadMessage={handleReadMessage}
  //                     handleDeleteNotification={handleDeleteNotification}
  //                   />
  //                 ))}
  //             </div>
  //           )}
  //         </div>
  //       </div>
  //     </div>
  //   </div>
  // );
  return (
    <>
      {isLoading ? (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
          <img src="/Images/loader.gif" className="h-16 w-16 bg-opacity-50" />
        </div>
      ) : (
        <div className="bg-[#F3F2EF] mt-16 py-6 min-h-screen">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="w-full lg:w-1/4 h-fit bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-base font-semibold mb-4">
                  Manage your Notifications
                </h2>

                <div className="flex items-start gap-1 mb-4">
                  <span className="text-2xl !font-black !leading-3 !text-black">
                    &rarr;
                  </span>
                  <p className="text-sm text-gray-600">
                    We'll keep your responses confidential and secure. They
                    won't be shared with recruiters and employers.
                  </p>
                </div>

                <div className="flex items-start gap-1">
                  <span className="text-2xl font-black !leading-3 text-black">
                    &rarr;
                  </span>
                  <p className="text-sm text-gray-600">
                    We'll keep your responses confidential and secure. They
                    won't be shared with recruiters and employers.
                  </p>
                </div>

                <a href="#" className="text-blue-600 text-sm mt-4 inline-block">
                  View Settings
                </a>
              </div>

              <div className="w-full lg:w-3/4">
                {recentCount > 0 && (
                  <div className="px-6">
                    <div className="flex items-center gap-8">
                      <hr className="w-full border-t border-gray-300 " />
                      <h2 className="text-lg text-center font-semibold">
                        Recent
                      </h2>
                      <hr className="w-full border-t border-gray-300 " />
                    </div>
                    {notifications
                      .filter((n) => n.type === "recent")
                      .map((n) => (
                        <MemoizedNotificationItem
                          key={n.id}
                          id={n.id}
                          icon={n.sender.profile}
                          text={n.message}
                          time={n.time}
                          name={n.sender.name}
                          status={n.read_status}
                          handleReadMessage={handleReadMessage}
                          handleDeleteNotification={handleDeleteNotification}
                        />
                      ))}
                  </div>
                )}
                {earlierCount > 0 && (
                  <div className="px-6 py-2">
                    <div className="flex items-center gap-8">
                      <hr className="w-full border-t border-gray-300" />
                      <h2 className="text-lg text-centter font-semibold">
                        Earlier
                      </h2>
                      <hr className="w-full border-t border-gray-300 " />
                    </div>
                    {notifications
                      .filter((n) => n.type === "earlier")
                      .map((n) => (
                        <NotificationItem
                          key={n.id}
                          id={n.id}
                          icon={n.sender.profile}
                          text={n.message}
                          time={n.time}
                          name={n.sender.name}
                          status={n.read_status}
                          handleReadMessage={handleReadMessage}
                          handleDeleteNotification={handleDeleteNotification}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Notifications;
